import type { AccountId } from "@redotech/effect-api-shared/ids/definitions";
import type { AccessibleSupportTeam } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";
import { useMemo } from "react";

const ACCESSIBLE_SUPPORT_TEAMS_STALE_MS = 5 * 60 * 1000;

export const accessibleSupportTeamsQueryKey = [
  "accessibleSupportTeams",
] as const;

/**
 * Every team whose tickets the caller can see — their home team plus any
 * connected team they have VIEW_SUPPORT on. Fetched once and cached for the
 * session; powers the optional "Team" column and the team filter.
 */
export function useAccessibleSupportTeams(): readonly AccessibleSupportTeam[] {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const query = useQuery({
    queryKey: accessibleSupportTeamsQueryKey,
    queryFn: async (): Promise<readonly AccessibleSupportTeam[]> => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        client.supportTicketRelatedRecords
          .getAccessibleSupportTeams({})
          .pipe(Effect.orDie),
      );
      return result.teams;
    },
    staleTime: ACCESSIBLE_SUPPORT_TEAMS_STALE_MS,
  });
  return query.data ?? [];
}

/**
 * `AccountId → AccessibleSupportTeam` map. Support-ticket rows carry their
 * `accountId`, so the team column resolves a display name with a direct
 * branded-key lookup (no string coercion).
 */
export function useAccessibleSupportTeamMap(): Map<
  AccountId,
  AccessibleSupportTeam
> {
  const teams = useAccessibleSupportTeams();
  return useMemo(() => {
    const map = new Map<AccountId, AccessibleSupportTeam>();
    for (const team of teams) {
      map.set(team.accountId, team);
    }
    return map;
  }, [teams]);
}

/**
 * True when the caller has at least one *linked* team beyond their own — the
 * gate for showing the team filter (a single-team merchant has nothing to
 * filter by).
 */
export function useHasLinkedSupportTeams(): boolean {
  const teams = useAccessibleSupportTeams();
  return teams.length > 1;
}
